import { useState, useEffect, useCallback, useRef } from 'react';
import type { ReactNode } from 'react';
import type { PaginatedResponse } from '../../../types';

interface PaginatedProps<T> {
  route: string;
  limit?: number;
  render: (
    item: T,
    index: number,
    items: T[],
    setItems: React.Dispatch<React.SetStateAction<T[]>>
  ) => ReactNode;
  emptyMessage?: string;
  // Optional callback to filter/transform items after fetch
  transform?: (items: T[]) => T[];
  // Optional deps to trigger refetch
  deps?: unknown[];
  // Optional flag to disable fetching (e.g., while auth is loading)
  disabled?: boolean;
}

function Paginated<T>({
  route,
  limit = 20,
  render,
  emptyMessage = 'No items',
  transform,
  deps = [],
  disabled = false,
}: PaginatedProps<T>) {
  const [items, setItems] = useState<T[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const fetchPage = useCallback(
    async (pageNum: number, append: boolean) => {
      setLoading(true);
      try {
        const url = `${route}${route.includes('?') ? '&' : '?'}page=${pageNum}&limit=${limit}`;
        const res = await fetch(url, { credentials: 'include' });
        if (!res.ok) throw new Error('Failed to fetch');
        const data: PaginatedResponse<T> = await res.json();
        const newItems = transform ? transform(data.items) : data.items;
        setItems((prev) => (append ? [...prev, ...newItems] : newItems));
        setHasMore(data.hasMore);
        setPage(pageNum);
      } catch {
        // Silently fail
      } finally {
        setLoading(false);
      }
    },
    [route, limit, transform]
  );

  // Reset and fetch first page when route or deps change
  useEffect(() => {
    if (disabled) return; // Skip fetching while disabled
    setItems([]);
    setPage(1);
    setHasMore(false);
    fetchPage(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [route, disabled, ...deps]);

  // IntersectionObserver for infinite scroll
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          fetchPage(page + 1, true);
        }
      },
      { rootMargin: '100px' }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loading, page, fetchPage]);

  if (items.length === 0 && !loading) {
    return (
      <p className="subtle-text" style={{ textAlign: 'center' }}>
        {emptyMessage}
      </p>
    );
  }

  return (
    <div>
      {items.map((item, index) => render(item, index, items, setItems))}
      <div ref={sentinelRef} style={{ height: '1px' }} />
      {loading && (
        <p
          className="subtle-text"
          style={{ textAlign: 'center', padding: '10px' }}
        >
          Loading...
        </p>
      )}
    </div>
  );
}

export default Paginated;
